import CreateNotificationModal from '@/components/molecules/CreateNotificationModal';
import CustomTable from '@/components/molecules/CustomTable';
import SearchWithDropdownFilter from '@/components/molecules/SearchWithDropdownFilter';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { MIN_CHARS_TO_DO_SEARCH } from '@/constants/numberVars';
import { NOTIFICATION_ACTIONS, NOTIFICATION_CHANNELS, NOTIFICATION_TYPES } from '@/constants/stringVars';
import useAllNotifications from '@/hooks/notifications/useAllNotifications';
import useSendNotificationToAllUsers from '@/hooks/notifications/useSendNotificationToAllUsers';
import { useTableColumnSort } from '@/hooks/useTableColumnSort';
import NotificationOverviewModal from '@/modules/notifications/NotificationOverviewModal';
import { TNotificationApi, TNotificationFilterOption } from '@/types/Notification.types';
import { displayCapitalizedUnderscoredString } from '@/utils/stringUtils';
import { TNotificationForm } from '@/validation/notification';
import { HStack, useDisclosure } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

const COLUMNS = ['Title', 'Message', 'Created At', 'Is Read', 'Channels', 'Type', 'Statuses'];
const COLUMNS_TO_SORT_NOTIFICATIONS_BY = ['Created At', 'Statuses', 'Title'] as const;

const Notifications = () => {
  const {
    isOpen: isCreateNotificationOpen,
    onOpen: onCreateNotificationOpen,
    onClose: onCreateNotificationClose,
  } = useDisclosure();

  const {
    isOpen: isNotificationOverviewOpen,
    onOpen: onNotificationOverviewOpen,
    onClose: onNotificationOverviewClose,
  } = useDisclosure();

  const [filterOption, setFilterOption] = useState<TNotificationFilterOption | undefined>();
  const [filterText, setFilterText] = useState('');

  const [selectedNotificationId, setSelectedNotificationId] = useState<string | undefined>();

  const { isPending, mutate } = useSendNotificationToAllUsers(onCreateNotificationClose);

  const { sortingColumn, sortingDirection, updateSortingColumn } = useTableColumnSort<string>({
    defaultSortingColumn: 'Created At',
  });

  const {
    isLoading,
    isRefetching,
    pagination,
    data: notifications,
  } = useAllNotifications({
    filterOption,
    filterText,
    sortingColumn,
    sortingDirection,
  });

  const isSearching = useMemo(
    () => filterOption !== undefined && filterText.length >= MIN_CHARS_TO_DO_SEARCH && isRefetching,
    [filterOption, filterText, isRefetching],
  );

  const data = {
    nodes:
      notifications?.map((notification) => ({
        ...notification,
        id: notification._id,
      })) ?? [],
  };

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item: (typeof data.nodes)[number]) => {
        return {
          id: item._id,
          cells: [
            item.title ?? '/',
            item.message ?? '/',
            item?.createdAt ?? '/',
            item?.isRead ? 'Yes' : 'No',
            item.channels?.map((channel) => displayCapitalizedUnderscoredString(channel)).join(', '),
            displayCapitalizedUnderscoredString(item.type ?? '/') ?? '/',
            item.statuses
              ? Object.entries(item.statuses)
                  .map(
                    ([key, value]) =>
                      `${displayCapitalizedUnderscoredString(key)}: ${displayCapitalizedUnderscoredString(value)}`,
                  )
                  .join(', ')
              : '/',
          ],
        };
      }),
    [data.nodes],
  );

  const onSubmit = (form: TNotificationForm) => {
    mutate(form);
  };

  return (
    <SidebarItemViewTemplate
      title="Notifications"
      buttonLabel="Send Notification"
      onButtonClick={onCreateNotificationOpen}
    >
      <HStack justifyContent={'flex-end'} width={'100%'} marginTop={'20px'}>
        <SearchWithDropdownFilter<TNotificationFilterOption | undefined>
          selectedFilterOption={filterOption}
          filterOptions={['Type', 'Is Read', 'Action', 'Channel']}
          filterOptionsSubOptions={[
            Object.values(NOTIFICATION_TYPES),
            Object.values(['Yes', 'No']),
            Object.values(NOTIFICATION_ACTIONS),
            Object.values(NOTIFICATION_CHANNELS),
          ]}
          filterText={filterText}
          setFilterOption={setFilterOption}
          setFilterText={setFilterText}
          isLoadingSearch={isSearching}
        />
      </HStack>
      <CustomTable<TNotificationApi[], string>
        noItemsMessage="No notifications found"
        isLoading={isLoading}
        isRefetching={isRefetching}
        pagination={pagination}
        fullWidth
        tableListItems={tableListItems}
        columns={COLUMNS}
        dataWithId={data.nodes}
        columnsToSortBy={COLUMNS_TO_SORT_NOTIFICATIONS_BY}
        sortingColumn={sortingColumn}
        sortingDirection={sortingDirection}
        updateSortingColumn={updateSortingColumn}
        hasRowSelect={true}
        customColumnGridPercentages={[15, 22, 20, 9, 9, 10, 15]}
        navigateOnRowSelect={false}
        onRowSelect={(id) => {
          setSelectedNotificationId(id);
          onNotificationOverviewOpen();
        }}
      />
      <CreateNotificationModal
        isOpen={isCreateNotificationOpen}
        onClose={onCreateNotificationClose}
        onSubmit={onSubmit}
        isLoading={isPending}
        title="Send Notification to All Users"
      />
      <NotificationOverviewModal
        isOpen={isNotificationOverviewOpen}
        onClose={onNotificationOverviewClose}
        id={selectedNotificationId}
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/notifications/')({
  component: Notifications,
});
